//src/services/tasks.js
import api from '../api/axios';

const BASE_URL = 'api/tasks';

/**
 * Helper: costruice una query string da un dict, saltando undefined/null
 * e serializzando i booleani come `true`/`false` (richiesto dal backend
 * per filtri come without_project, is_completed, has_assignee).
 */
const buildQuery = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'boolean') {
      q.append(k, v ? 'true' : 'false');
    } else {
      q.append(k, v);
    }
  });
  return q.toString();
};

// Accetta: { project, without_project, status, limit, offset, search, ... }.
// Il backend è paginato (LimitOffsetPagination) ma senza params risponde comunque
// con la stessa shape { count, next, previous, results }. Usa extractTasksFromResponse
// per normalizzare la response.
// Pagina Task DEVE passare sempre `project` o `without_project` (FE-2). I consumer
// generali (widget, agenda, ecc.) possono non passare scope (FE-7).
export const getTasks = (params = {}) => {
  const qs = buildQuery(params);
  return api.get(`${BASE_URL}/tasks/${qs ? `?${qs}` : ''}`);
};

// Normalizza il body della risposta: supporta sia array legacy sia {count,next,previous,results}.
export const extractTasksFromResponse = (data) => {
  if (Array.isArray(data)) return { tasks: data, count: data.length, hasMore: false };
  if (data && Array.isArray(data.results)) {
    return {
      tasks: data.results,
      count: typeof data.count === 'number' ? data.count : data.results.length,
      hasMore: data.next !== null && data.next !== undefined,
    };
  }
  return { tasks: [], count: 0, hasMore: false };
};

export const getTaskById = (id) => {
  return api.get(`${BASE_URL}/tasks/${id}/`);
};

export const createTask = (data) => {
  return api.post(`${BASE_URL}/tasks/`, data);
};

export const updateTask = (id, data) => {
  return api.patch(`${BASE_URL}/tasks/${id}/`, data);
};

export const deleteTask = (id) => {
  return api.delete(`${BASE_URL}/tasks/${id}/`);
};

export const toggleTaskCompleted = async (taskId) => {
  const response = await api.patch(`${BASE_URL}/tasks/${taskId}/toggle-completed/`);
  return response.data;
};

/**
 * Spostamento atomico di un task (BE 6.3).
 * Unica action per cambio progetto, cambio colonna, riordino e move-to-inbox.
 * Ritorna la response completa con location/project_detail/status_detail.
 *
 * @param {number} taskId
 * @param {{ project: number|null, status: number|null, position: number,
 *           expected_source_project?: number|null, expected_source_status?: number|null }} payload
 */
export const moveTask = async (taskId, payload) => {
  const response = await api.post(`${BASE_URL}/tasks/${taskId}/move/`, payload);
  return response.data;
};

/**
 * Statistiche server-side per progetto o inbox (BE 6.4).
 * Calcolate con aggregazioni DB, mai sul dataset client paginato.
 *
 * @param {{ projectId?: number, withoutProject?: boolean }} scope
 */
export const getTaskSummary = async ({ projectId, withoutProject } = {}) => {
  const params = {};
  if (withoutProject) {
    params.without_project = true;
  } else if (projectId !== undefined && projectId !== null) {
    params.project = projectId;
  }
  const qs = buildQuery(params);
  const response = await api.get(`${BASE_URL}/tasks/summary/${qs ? `?${qs}` : ''}`);
  return response.data;
};

export const reorderTasks = async (positions) => {
  const response = await api.patch(`${BASE_URL}/tasks/reorder/`, { positions });
  return response.data;
};

// Funzione per ottenere tutta la cronologia dei task (bulk)
export const getTasksHistory = (params = {}) => {
  return api.get(`${BASE_URL}/taskhistory/`, { params });
};

// Funzione per ottenere la cronologia di un singolo task
export const getTaskHistory = (taskId) => {
  return api.get(`${BASE_URL}/task-history/`, { params: { task_id: taskId } });
};

// Nuove funzioni per gestire gli allegati
export const uploadTaskAttachment = (taskId, fileData) => {
  const formData = new FormData();
  formData.append('file', fileData);
  
  return api.post(`${BASE_URL}/tasks/${taskId}/add_attachment/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getTaskAttachments = (taskId) => {
  return api.get(`${BASE_URL}/task-attachments/`, { params: { task: taskId } });
};

export const deleteTaskAttachment = (attachmentId) => {
  return api.delete(`${BASE_URL}/task-attachments/${attachmentId}/`);
};

/**
 * Colonne di un progetto (BE 6.2).
 * Passare sempre projectId dalla pagina Task. Senza projectId usa il nuovo
 * endpoint /task-statuses/ senza filtro (per consumer legacy che vogliono
 * tutte le colonne del tenant).
 *
 * @param {number} [projectId]
 */
export const getTaskStatuses = (projectId) => {
  const params = {};
  if (projectId !== undefined && projectId !== null) {
    params.project = projectId;
  }
  const qs = buildQuery(params);
  return api.get(`${BASE_URL}/task-statuses/${qs ? `?${qs}` : ''}`);
};

export const createTaskStatus = (data) => {
  return api.post(`${BASE_URL}/task-statuses/`, data);
};

export const updateTaskStatus = (id, data) => {
  return api.patch(`${BASE_URL}/task-statuses/${id}/`, data);
};

/**
 * Elimina una colonna VUOTA (BE 6.2).
 *
 * IMPORTANTE: il vecchio parametro `force=true` è stato rimosso dal contratto.
 * Per eliminare una colonna che contiene task usare `moveAndDeleteTaskStatus`
 * con una colonna di destinazione. Il parametro `opts.force` è ignorato
 * (deprecation silenziosa per non rompere i caller non ancora migrati in FE-6).
 *
 * @param {number} id
 * @param {object} [opts] Ignored (legacy compatibility).
 */
export const deleteTaskStatus = (id, opts = {}) => {
  // Ignoriamo opts.force: l'endpoint distruttivo è rimosso dal contratto nuovo.
  void opts;
  return api.delete(`${BASE_URL}/task-statuses/${id}/`);
};

/**
 * Elimina una colonna spostando prima tutti i suoi task in una destinazione
 * dello stesso progetto (BE 6.2 / move-and-delete). Transazione atomica.
 *
 * @param {number} statusId Colonna da eliminare.
 * @param {number} destinationStatusId Colonna destinazione (stesso progetto, attiva, diversa).
 */
export const moveAndDeleteTaskStatus = (statusId, destinationStatusId) => {
  return api.post(`${BASE_URL}/task-statuses/${statusId}/move-and-delete/`, {
    destination_status: destinationStatusId,
  });
};

/**
 * Riordina colonne di un progetto (BE 6.2 / reorder-columns).
 * Una sola action batch, transazionale. Salva ordini 10,20,30,...
 *
 * @param {number} projectId
 * @param {number[]} columnIds Ordine visuale completo delle colonne attive.
 */
export const reorderTaskColumns = (projectId, columnIds) => {
  return api.post(`${BASE_URL}/projects/${projectId}/reorder-columns/`, {
    column_ids: columnIds,
  });
};

// Verifica se uno stato è utilizzato da qualche task (legacy, per colonna vuota).
export const checkTaskStatusInUse = async (statusId) => {
  return await api.get(`${BASE_URL}/task-statuses/${statusId}/in-use/`);
};

// Funzioni per gestire gli allegati temporanei dei task
export const uploadTempTaskAttachment = (fileData, sessionKey = null) => {
  const formData = new FormData();
  formData.append('file', fileData);
  if (sessionKey) {
    formData.append('session_key', sessionKey);
  }
  
  return api.post(`${BASE_URL}/tasks/upload_temp_attachment/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const getTempTaskAttachments = (sessionKey) => {
  return api.get(`${BASE_URL}/tasks/get_temp_attachments/`, { 
    params: { session_key: sessionKey } 
  });
};

export const deleteTempTaskAttachment = (attachmentId) => {
  return api.delete(`${BASE_URL}/tasks/delete_temp_attachment/`, { 
    params: { attachment_id: attachmentId } 
  });
};

export const cleanupTempTaskAttachments = (sessionKey) => {
  return api.delete(`${BASE_URL}/tasks/cleanup_temp_attachments/`, { 
    params: { session_key: sessionKey } 
  });
};